type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

/** Profitability screens (`fin.profit` = customers, `fin.route` = routes). */
export const profitPl = {
  profit: {
    subtitle: 'Ile naprawdę na nich zarobiliśmy: sprzedaż − koszt towaru − transport − koszty trasy.',
    tabs: { customers: 'Klienci', routes: 'Trasy' },
    kpiSection: 'Rentowność klientów',
    customersCount: '{count} klientów w zakresie',
    marginPctOf: 'Marża {value} sprzedaży',
    routeCosts: 'Koszty trasy',
    routeCostsSplit: 'Transport {transport} · inne {other}',
    afterAll: 'Po wszystkich kosztach',
    vsMargin: 'Marża handlowa: {value}',
    lossCustomers: 'Klienci ze stratą',
    lossValue: 'Strata łącznie {value} · niska rentowność: {low}',
    noLoss: 'Żaden klient nie jest na minusie',
    class: { PROFITABLE: 'Rentowny', LOW: 'Niska rentowność', LOSS: 'Strata' },
    seg: { cogs: 'Koszt towaru', transport: 'Transport', other: 'Inne koszty', profit: 'Zysk', loss: 'Strata' },
    flow: {
      title: 'Gdzie poszły pieniądze',
      subtitle: 'Każde {sales} sprzedaży rozkłada się na koszt towaru, transport, inne koszty i zysk.',
      classes: 'Klienci wg rentowności',
    },
    atRisk: {
      title: 'Klienci do przeglądu',
      subtitle: 'Strata lub niska rentowność — z powodami.',
      none: 'Brak klientów ze stratą lub niską rentownością',
    },
    filter: { all: 'Wszyscy', low: 'Niska rent.', loss: 'Strata' },
    table: {
      title: 'Ranking rentowności klientów',
      subtitle: 'Rzeczywisty zysk po koszcie towaru, transporcie i innych kosztach.',
      customer: 'Klient',
      sales: 'Sprzedaż',
      where: 'Gdzie poszły pieniądze',
      cogs: 'Koszt towaru',
      transport: 'Transport',
      other: 'Inne',
      profit: 'Zysk rzecz.',
      profitPct: 'Rentowność',
      empty: 'Brak klientów dla wybranego filtra',
      legend: 'Legenda',
    },
  },
  route: {
    kpiSection: 'Rentowność tras',
    cardsSection: 'Trasy',
    revenue: 'Przychód',
    costLower: 'koszt',
    marginLower: 'marża',
    profitabilityLower: 'rentowność',
    routeCost: 'Koszt trasy',
    profit: 'Zysk trasy',
    profitability: 'Rentowność',
    ofRevenue: 'przychodu',
    routesCount: '{count} tras',
    deliveriesN: '{count} dostaw',
    failed: 'Nieudane dostawy: {count}',
    noKm: 'Brak kilometrów',
    costPerKmValue: '{value} zł/km',
    unrouted: 'Poza trasami: przychód {revenue}, marża {margin} (FV bez dostawy lub klienci bez trasy).',
    empty: 'Brak tras z dostawami w tym okresie',
    class: { STAR: 'Gwiazda', OK: 'Dobra', WEAK: 'Słaba', LOSS: 'Strata' },
    table: {
      title: 'Tabela tras',
      subtitle: 'Przychód → koszt towaru → marża → koszt trasy → zysk, z danymi operacyjnymi.',
      route: 'Trasa',
      deliveries: 'Dostawy',
      km: 'Km',
      costPerKm: 'Koszt/km',
      onTime: 'Na czas',
    },
    cost: {
      title: 'Struktura kosztu trasy',
      subtitle: 'Paliwo, kierowca, pojazd i pozostałe koszty.',
      fuel: 'Paliwo',
      driver: 'Kierowca',
      vehicle: 'Pojazd',
      other: 'Inne',
    },
    weekly: {
      title: 'Trend tygodniowy',
      subtitle: 'Marża i koszt trasy (słupki) oraz rentowność % (linia) w kolejnych tygodniach.',
      week: 'Tydzień od {date}',
    },
    customers: {
      title: 'Klienci trasy',
      subtitle: 'Kto zarabia, a kto obciąża trasę.',
      profitShare: 'Udział w zysku',
    },
    map: {
      title: 'Mapa trasy',
      subtitle: 'Klienci jako punkty: wielkość = przychód, kolor = rentowność.',
      pick: 'Wybierz trasę',
      depot: 'Magazyn',
      good: 'Rentowny (≥ 10%)',
      warn: 'Niska rentowność (0–10%)',
      bad: 'Strata',
      hint: 'Linia pokazuje orientacyjną kolejność odwiedzin (najbliższy sąsiad od magazynu).',
      empty: 'Brak współrzędnych klientów tej trasy',
      missing: '{count} klientów trasy bez współrzędnych — nie ma ich na mapie.',
    },
    detail: {
      title: 'Trasa',
      subtitle: 'Szczegóły rentowności trasy',
      customersLink: 'Klienci trasy',
    },
  },
};

export const profitEn: Shape<typeof profitPl> = {
  profit: {
    subtitle: 'How much we really earned on them: sales − cost of goods − transport − route costs.',
    tabs: { customers: 'Customers', routes: 'Routes' },
    kpiSection: 'Customer profitability',
    customersCount: '{count} customers in scope',
    marginPctOf: '{value} margin on sales',
    routeCosts: 'Route costs',
    routeCostsSplit: 'Transport {transport} · other {other}',
    afterAll: 'After all costs',
    vsMargin: 'Trade margin: {value}',
    lossCustomers: 'Loss-making customers',
    lossValue: 'Total loss {value} · low profitability: {low}',
    noLoss: 'No customer is in the red',
    class: { PROFITABLE: 'Profitable', LOW: 'Low profitability', LOSS: 'Loss' },
    seg: { cogs: 'Cost of goods', transport: 'Transport', other: 'Other costs', profit: 'Profit', loss: 'Loss' },
    flow: {
      title: 'Where the money went',
      subtitle: 'Every {sales} of sales splits into cost of goods, transport, other costs and profit.',
      classes: 'Customers by profitability',
    },
    atRisk: {
      title: 'Customers to review',
      subtitle: 'Loss or low profitability — with reasons.',
      none: 'No customers with a loss or low profitability',
    },
    filter: { all: 'All', low: 'Low prof.', loss: 'Loss' },
    table: {
      title: 'Customer profitability ranking',
      subtitle: 'Real profit after cost of goods, transport and other costs.',
      customer: 'Customer',
      sales: 'Sales',
      where: 'Where the money went',
      cogs: 'Cost of goods',
      transport: 'Transport',
      other: 'Other',
      profit: 'Real profit',
      profitPct: 'Profitability',
      empty: 'No customers for the selected filter',
      legend: 'Legend',
    },
  },
  route: {
    kpiSection: 'Route profitability',
    cardsSection: 'Routes',
    revenue: 'Revenue',
    costLower: 'cost',
    marginLower: 'margin',
    profitabilityLower: 'profitability',
    routeCost: 'Route cost',
    profit: 'Route profit',
    profitability: 'Profitability',
    ofRevenue: 'of revenue',
    routesCount: '{count} routes',
    deliveriesN: '{count} deliveries',
    failed: 'Failed deliveries: {count}',
    noKm: 'No kilometres',
    costPerKmValue: '{value} PLN/km',
    unrouted: 'Outside routes: revenue {revenue}, margin {margin} (invoices without delivery or customers without a route).',
    empty: 'No routes with deliveries in this period',
    class: { STAR: 'Star', OK: 'Good', WEAK: 'Weak', LOSS: 'Loss' },
    table: {
      title: 'Route table',
      subtitle: 'Revenue → cost of goods → margin → route cost → profit, with operational data.',
      route: 'Route',
      deliveries: 'Deliveries',
      km: 'Km',
      costPerKm: 'Cost/km',
      onTime: 'On time',
    },
    cost: {
      title: 'Route cost structure',
      subtitle: 'Fuel, driver, vehicle and other costs.',
      fuel: 'Fuel',
      driver: 'Driver',
      vehicle: 'Vehicle',
      other: 'Other',
    },
    weekly: {
      title: 'Weekly trend',
      subtitle: 'Margin and route cost (bars) with profitability % (line) week by week.',
      week: 'Week of {date}',
    },
    customers: {
      title: 'Route customers',
      subtitle: 'Who earns and who weighs the route down.',
      profitShare: 'Profit share',
    },
    map: {
      title: 'Route map',
      subtitle: 'Customers as points: size = revenue, colour = profitability.',
      pick: 'Choose a route',
      depot: 'Depot',
      good: 'Profitable (≥ 10%)',
      warn: 'Low profitability (0–10%)',
      bad: 'Loss',
      hint: 'The line shows an indicative visiting order (nearest neighbour from the depot).',
      empty: 'No coordinates for this route’s customers',
      missing: '{count} route customers have no coordinates — not shown on the map.',
    },
    detail: {
      title: 'Route',
      subtitle: 'Route profitability details',
      customersLink: 'Route customers',
    },
  },
};
